import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DialogHeader, DialogTitle } from '../../components/ui/dialog';
import GuardedDialog from '../../components/GuardedDialog';
import useFormDirtyState from '../../hooks/useFormDirtyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Search, Package, Building2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import ProductForm from '../../components/forms/ProductForm';
import { DestructiveActionDialog } from '../../lib/DestructiveActionDialog';
import AdminDistributionsManager from '../../components/admin/AdminDistributionsManager';
import AdminOriginalPublisherManager from '../../components/admin/AdminOriginalPublisherManager';
import DistributionAttachForm from '../../components/forms/DistributionAttachForm';
import resolveProduct from '../../domain/product/resolveProduct';

/**
 * AdminProducts V4 — Distribution-first architecture
 * 
 * Key changes:
 * - Distributions editable during product CREATION (pending mode)
 * - Editors moved into ProductForm via slot
 * - Legacy distributor selector removed
 */

const getInitialFormData = () => ({
  intitule: '', code_ean: '', tags: '',
  partner_id: '', auteur: '', illustrateur: '',
  description_courte: '', descriptif: '', liste_materiel: '',
  nb_joueurs_min: '', nb_joueurs_max: '', age_minimum: '',
  temps_moyen_partie: '', type_jeu: '', public_cible: '',
  mecanisme: '', tags_produit_ids: [], langue: '', pays_provenance: '',
  prix_vente_conseille: '', tva: '',
  taille_boite: '', poids_boite: '',
  date_sortie_iso: '', date_debut_selection_iso: '', date_fin_selection_iso: '',
  visuels: [], atouts_1: '', atouts_2: '', atouts_3: '',
  plan_communication_commercial: '', video_url: '', regle_jeu_url: '',
  pourquoi_referencer: '',
  is_active: true, download_fiche_enabled: true
});

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());
  const { hasUnsavedChanges, setDirty, resetDirty } = useFormDirtyState();

  // === PENDING DISTRIBUTIONS (creation mode) ===
  const [pendingDistributions, setPendingDistributions] = useState([]);
  // === PENDING PUBLISHERS (creation mode — same pattern as distributions) ===
  const [pendingPublishers, setPendingPublishers] = useState([]);
  const distManagerRef = useRef(null);

  useEffect(() => {
    loadProducts();
    loadPartners();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await api.get('/products?admin=true&limit=10000');
      const data = response.data;
      setProducts(Array.isArray(data) ? data : (data?.products || []));
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      // Phase 2.2: Load only distributors for the distribution selector
      const response = await api.get('/partners?type=distributor');
      const sorted = (response.data || []).slice().sort((a, b) => {
        const nameA = (a.nom || '').toLowerCase().trim();
        const nameB = (b.nom || '').toLowerCase().trim();
        return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
      });
      setPartners(sorted);
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const getPartnerById = useCallback((partnerId) => {
    if (!partnerId) return null;
    return partners.find(p => p.id === partnerId || p.slug === partnerId) || null;
  }, [partners]);

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        intitule: product.intitule || '', code_ean: product.code_ean || '',
        tags: product.tags?.join(', ') || '',
        partner_id: product.partner_id || '',
        auteur: product.auteur || '', illustrateur: product.illustrateur || '',
        description_courte: product.description_courte || '',
        descriptif: product.descriptif || '', liste_materiel: product.liste_materiel || '',
        nb_joueurs_min: product.nb_joueurs_min ?? '', nb_joueurs_max: product.nb_joueurs_max ?? '',
        age_minimum: product.age_minimum ?? '', temps_moyen_partie: product.temps_moyen_partie ?? '',
        type_jeu: product.type_jeu || '', public_cible: product.public_cible || '',
        mecanisme: product.mecanisme || '', tags_produit_ids: product.tags_produit_ids || [], langue: product.langue || '',
        pays_provenance: product.pays_provenance || '',
        prix_vente_conseille: product.prix_vente_conseille ?? '', tva: product.tva ?? '',
        taille_boite: product.taille_boite || '', poids_boite: product.poids_boite ?? '',
        date_sortie_iso: (product.date_sortie_iso || '').slice(0, 10),
        date_debut_selection_iso: (product.date_debut_selection_iso || '').slice(0, 10),
        date_fin_selection_iso: (product.date_fin_selection_iso || '').slice(0, 10),
        visuels: product.visuels || [],
        atouts_1: product.atouts_1 || '', atouts_2: product.atouts_2 || '',
        atouts_3: product.atouts_3 || '',
        plan_communication_commercial: product.plan_communication_commercial || '',
        video_url: product.video_url || '', regle_jeu_url: product.regle_jeu_url || '',
        pourquoi_referencer: product.pourquoi_referencer || '',
        is_active: product.is_active ?? true,
        download_fiche_enabled: product.download_fiche_enabled ?? false
      });
      setPendingDistributions([]);
    } else {
      setEditingProduct(null);
      setFormData(getInitialFormData());
      setPendingDistributions([]);
    }
    setShowDialog(true);
    resetDirty();
  };

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirty();
  }, [setDirty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // === Phase 2.2: Validation champs obligatoires ===
      if (!formData.code_ean?.trim()) {
        toast.error('Le code EAN est obligatoire');
        setSaving(false);
        return;
      }
      if (!formData.descriptif?.trim()) {
        toast.error('La description complète est obligatoire');
        setSaving(false);
        return;
      }
      // Validation ≥1 éditeur
      if (!editingProduct && pendingPublishers.length === 0) {
        toast.error('Au moins un éditeur est obligatoire');
        setSaving(false);
        return;
      }
      // Validation ≥1 distributeur (création uniquement)
      if (!editingProduct && pendingDistributions.length === 0) {
        toast.error('Au moins un distributeur est obligatoire');
        setSaving(false);
        return;
      }

      // === STEP 1: Validate and save all mechanics (distributions) ===
      if (distManagerRef.current?.validateAndSaveAllMechanics) {
        const mechOk = await distManagerRef.current.validateAndSaveAllMechanics();
        if (!mechOk) {
          toast.error('Veuillez corriger les erreurs dans les offres avant d\u2019enregistrer');
          setSaving(false);
          return;
        }
      }

      // === STEP 2: Save the product ===
      const submitData = {
        intitule: formData.intitule,
        code_ean: formData.code_ean || null,
        tags: typeof formData.tags === 'string'
          ? formData.tags.split(',').map(t => t.trim()).filter(t => t)
          : (formData.tags || []),
        partner_id: formData.partner_id || null,
        auteur: formData.auteur || null,
        illustrateur: formData.illustrateur || null,
        description_courte: formData.description_courte || null,
        descriptif: formData.descriptif || '',
        liste_materiel: formData.liste_materiel || null,
        nb_joueurs_min: formData.nb_joueurs_min !== '' ? parseInt(formData.nb_joueurs_min) : null,
        nb_joueurs_max: formData.nb_joueurs_max !== '' ? parseInt(formData.nb_joueurs_max) : null,
        age_minimum: formData.age_minimum !== '' ? parseInt(formData.age_minimum) : null,
        temps_moyen_partie: formData.temps_moyen_partie !== '' ? parseInt(formData.temps_moyen_partie) : null,
        type_jeu: formData.type_jeu || null,
        public_cible: formData.public_cible || null,
        mecanisme: formData.mecanisme || null,
        tags_produit_ids: formData.tags_produit_ids || [],
        langue: formData.langue || null,
        pays_provenance: formData.pays_provenance || null,
        prix_vente_conseille: formData.prix_vente_conseille !== '' ? parseFloat(formData.prix_vente_conseille) : null,
        tva: formData.tva !== '' ? parseFloat(formData.tva) : null,
        taille_boite: formData.taille_boite || null,
        poids_boite: formData.poids_boite !== '' ? parseFloat(formData.poids_boite) : null,
        date_sortie_iso: formData.date_sortie_iso || null,
        date_debut_selection_iso: formData.date_debut_selection_iso || null,
        date_fin_selection_iso: formData.date_fin_selection_iso || null,
        visuels: Array.isArray(formData.visuels)
          ? formData.visuels
          : (typeof formData.visuels === 'string' && formData.visuels.trim()
            ? formData.visuels.split(',').map(v => v.trim()).filter(v => v)
            : []),
        atouts_1: formData.atouts_1 || null,
        atouts_2: formData.atouts_2 || null,
        atouts_3: formData.atouts_3 || null,
        plan_communication_commercial: formData.plan_communication_commercial || null,
        pourquoi_referencer: formData.pourquoi_referencer || null,
        video_url: formData.video_url || null,
        regle_jeu_url: formData.regle_jeu_url || null,
        is_active: formData.is_active,
        download_fiche_enabled: formData.download_fiche_enabled ?? false
      };

      let productId;

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, submitData);
        productId = editingProduct.id;
        toast.success('Produit modifié avec succès');
      } else {
        // === CREATION: POST product then POST pending distributions ===
        const res = await api.post('/products', submitData);
        productId = res.data?.id;
        toast.success('Produit créé avec succès');

        // Create pending distributions
        if (pendingDistributions.length > 0 && productId) {
          let distCreated = 0;
          for (const pd of pendingDistributions) {
            try {
              const distPayload = { ...pd, product_id: productId };
              delete distPayload.id; // Remove temp id
              await api.post(`/products/${productId}/distributions`, distPayload);
              distCreated++;
            } catch (err) {
              console.error('Error creating distribution:', err);
            }
          }
          if (distCreated > 0) {
            toast.success(`${distCreated} distributeur(s) créé(s)`);
          }
        }
        
        // Create pending publishers
        if (pendingPublishers.length > 0 && productId) {
          let pubCreated = 0;
          for (const pub of pendingPublishers) {
            try {
              await api.post(`/products/${productId}/publishers`, {
                product_id: productId,
                partner_id: pub.partner_id,
                role: 'publisher',
              });
              pubCreated++;
            } catch (err) {
              console.error('Error creating publisher link:', err);
            }
          }
          if (pubCreated > 0) {
            toast.success(`${pubCreated} éditeur(s) rattaché(s)`);
          }
        }
      }

      setShowDialog(false);
      resetDirty();
      setPendingDistributions([]);
      setPendingPublishers([]);
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // === ATTACH DISTRIBUTION (EAN rattachement) ===
  const [attachTarget, setAttachTarget] = useState(null);
  const [attachSubmitting, setAttachSubmitting] = useState(false);

  const handleAttachDistribution = (existingProductId, lookupProduct) => {
    if (lookupProduct) {
      resetDirty();
      setShowDialog(false);
      setTimeout(() => setAttachTarget(lookupProduct), 100);
    } else {
      setAttachTarget({ id: existingProductId, intitule: 'Produit', code_ean: '', editeur: '' });
    }
  };

  const handleAttachSubmit = async (productId, distributionData) => {
    setAttachSubmitting(true);
    try {
      if (formData.partner_id) {
        distributionData.partner_id = formData.partner_id;
      }
      await api.post(`/products/${productId}/distributions`, distributionData);
      toast.success('Distribution créée avec succès');
      setAttachTarget(null);
      loadProducts();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('déjà associé')) {
        toast.error('Ce distributeur est déjà associé à ce produit');
      } else {
        toast.error(detail || 'Erreur lors de la création');
      }
    } finally {
      setAttachSubmitting(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success('Produit supprimé');
      setDeleteTarget(null);
      loadProducts();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredProducts = useMemo(() =>
    products.filter(product =>
      product.intitule?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resolveProduct(product).editors.displayLabel?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.code_ean?.toLowerCase().includes(searchQuery.toLowerCase())
    ), [products, searchQuery]
  );

  const isCreating = !editingProduct;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold" data-testid="admin-products-title">
              Gestion des Produits
            </h1>
            <p className="text-muted-foreground mt-2">
              {products.length} produit{products.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="add-product-button">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un produit
          </Button>
        </div>

        {/* Search */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par désignation, éditeur ou EAN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-products-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Désignation</TableHead>
                  <TableHead>Éditeur</TableHead>
                  <TableHead>Distributeur</TableHead>
                  <TableHead>Prix Achat</TableHead>
                  <TableHead>PVC</TableHead>
                  <TableHead>EAN</TableHead>
                  <TableHead>Actif</TableHead>
                  <TableHead>Disponibilité</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Aucun produit trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => (
                    <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                      <TableCell className="font-medium max-w-[200px] truncate" title={product.intitule}>
                        {product.intitule}
                      </TableCell>
                      <TableCell>{(() => { const re = resolveProduct(product).editors; return re.displayLabel || '-'; })()}</TableCell>
                      <TableCell>
                        {(() => {
                          // Source de vérité: distributions (architecture Distribution-first)
                          const dists = product.distributions || [];
                          if (dists.length > 0) {
                            const names = dists
                              .map(d => d.partner_nom)
                              .filter(Boolean);
                            if (names.length > 0) {
                              return (
                                <span className="flex items-center gap-1" title={names.join(', ')}>
                                  <Building2 className="h-3 w-3 text-blue-500" />
                                  <span className="truncate max-w-[140px]">{names.join(', ')}</span>
                                </span>
                              );
                            }
                          }
                          // Fallback: legacy partner_id (anciens produits)
                          if (product.partner_id) {
                            const partner = getPartnerById(product.partner_id);
                            return partner ? (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3 text-blue-500" />
                                {partner.nom}
                              </span>
                            ) : '-';
                          }
                          return '-';
                        })()}
                      </TableCell>
                      <TableCell>{(() => { const rp = resolveProduct(product).pricing; return rp.hasAnyPrice ? `${rp.price.toFixed(2)}\u20AC` : rp.priceLabel; })()}</TableCell>
                      <TableCell>{product.prix_vente_conseille?.toFixed(2) || '-'}&euro;</TableCell>
                      <TableCell className="font-mono text-xs">{product.code_ean || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs rounded-full ${product.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {product.is_active ? 'Oui' : 'Non'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          (() => {
                            const dists = product.distributions || [];
                            if (dists.length === 0) return 'bg-gray-100 text-gray-500';
                            if (dists.some(d => d.availability_status === 'available')) return 'bg-green-100 text-green-700';
                            if (dists.every(d => d.availability_status === 'unavailable')) return 'bg-red-100 text-red-700';
                            return 'bg-gray-100 text-gray-500';
                          })()
                        }`}>
                          {(() => {
                            const dists = product.distributions || [];
                            if (dists.length === 0) return 'N/C';
                            if (dists.some(d => d.availability_status === 'available')) return 'En stock';
                            if (dists.every(d => d.availability_status === 'unavailable')) return 'Indisponible';
                            return 'N/C';
                          })()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(product)} data-testid={`edit-product-${product.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(product)} data-testid={`delete-product-${product.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════
            DIALOG — Product Form + Distributions + Editors
        ═══════════════════════════════════════════════════════════ */}
        <GuardedDialog open={showDialog} onOpenChange={setShowDialog} isDirty={hasUnsavedChanges} className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {editingProduct ? `Modifier: ${editingProduct.intitule}` : 'Ajouter un produit'}
              {hasUnsavedChanges && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium" data-testid="unsaved-indicator">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Modifications non enregistr&eacute;es
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* ProductForm — with editors + distributions slots */}
          <ProductForm
            role="admin"
            formData={formData}
            onFormChange={handleFormChange}
            partners={partners}
            editingProduct={editingProduct}
            onSubmit={handleSubmit}
            onCancel={() => setShowDialog(false)}
            saving={saving}
            onAttachDistribution={handleAttachDistribution}
            editorsSlot={(
              <AdminOriginalPublisherManager 
                productId={editingProduct?.id || null}
                pendingPublishers={pendingPublishers}
                onPendingChange={setPendingPublishers}
              />
            )}
            distributionsSlot={
              <AdminDistributionsManager
                ref={distManagerRef}
                productId={editingProduct?.id || null}
                currentPartnerId={formData.partner_id}
                partners={partners}
                onPrimaryChange={(newPartnerId) => handleFormChange('partner_id', newPartnerId)}
                pendingDistributions={isCreating ? pendingDistributions : undefined}
                onPendingChange={isCreating ? setPendingDistributions : undefined}
                onDirty={setDirty}
              />
            }
          />
        </GuardedDialog>

        {/* Mini-formulaire rattachement distribution (EAN lookup) */}
        <DistributionAttachForm
          open={!!attachTarget}
          onClose={() => setAttachTarget(null)}
          product={attachTarget}
          onSubmit={handleAttachSubmit}
          submitting={attachSubmitting}
        />

        <DestructiveActionDialog
          open={!!deleteTarget}
          onOpenChange={() => setDeleteTarget(null)}
          title="Supprimer le produit"
          itemName={deleteTarget?.intitule || ''}
          description="Le produit et toutes ses données associées seront définitivement supprimés."
          cascadeWarning="Les distributions, liens éditeurs et mécaniques promotionnelles liés à ce produit seront également supprimés."
          onConfirm={handleDelete}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminProducts;
